<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';

import Avatar from 'next/avatar/Avatar.vue';
import EmojiGlyph from 'shared/components/emoji/EmojiGlyph.vue';
import WhatsAppEmojiPicker from 'dashboard/components/widgets/conversation/WhatsAppEmojiPicker.vue';
import { useMapGetter } from 'dashboard/composables/store';

// Aceleres: detalhe das reacoes no padrao WhatsApp. O card abre ANCORADO na
// chip de reacao da mensagem (nao centralizado), lista quem reagiu e permite
// remover ou trocar a propria reacao. A API de reacao ja e um toggle por
// usuario (mesmo emoji remove, emoji diferente substitui), entao tudo aqui
// reaproveita o mesmo endpoint, sem rota nova.
const props = defineProps({
  reactions: {
    type: Array,
    default: () => [],
  },
  currentUserId: {
    type: Number,
    default: null,
  },
  // Elemento da chip que abriu o card. Guardamos o ELEMENTO, e nao um
  // retangulo ja medido: o card e position:fixed, portanto vive em
  // coordenadas de viewport, e uma medicao feita no clique deixa de valer
  // assim que a lista rola ou o layout mexe (uma imagem a acabar de
  // carregar chega). Medindo a partir do elemento, o card acompanha a chip.
  anchorEl: {
    type: Object,
    default: null,
  },
  // 'right' = mensagem enviada (bolha a direita). Decide para que lado o
  // card cresce a partir do clique.
  orientation: {
    type: String,
    default: 'left',
  },
});

const emit = defineEmits(['close', 'react']);

const agents = useMapGetter('agents/getAgents');
const selectedChat = useMapGetter('getSelectedChat');

const showPicker = ref(false);

const CARD_WIDTH = 360;
const CARD_MAX_HEIGHT = 380;
const GAP = 8;
const EDGE = 8;
// Quanto o card se afasta para a direita do ponto de clique. Num só sítio
// para ser fácil de afinar depois.
const SHIFT_RIGHT = 200;

const isOwnReaction = reaction =>
  !reaction?.from_contact &&
  props.currentUserId != null &&
  Number(reaction?.user_id) === Number(props.currentUserId);

const ownEmoji = computed(
  () => props.reactions.find(isOwnReaction)?.emoji || null
);

// Guarda o ultimo emoji que foi meu enquanto o card esta aberto. Sem isso, ao
// zerar a propria reacao a chip deixaria de ser "minha" e nao daria pra
// clicar de novo pra voltar a 1.
const lastOwnEmoji = ref(null);

const distinctEmojis = list => {
  const seen = [];
  list.forEach(reaction => {
    const { emoji } = reaction || {};
    if (emoji && !seen.includes(emoji)) seen.push(emoji);
  });
  return seen;
};

// As chips vistas na abertura ficam fixas: quando a propria reacao vai a zero
// a chip continua na tela mostrando 0, em vez de sumir.
const pinnedEmojis = ref([]);

const chips = computed(() => {
  const order = [...pinnedEmojis.value];
  distinctEmojis(props.reactions).forEach(emoji => {
    if (!order.includes(emoji)) order.push(emoji);
  });
  return order.map(emoji => ({
    emoji,
    count: props.reactions.filter(reaction => reaction?.emoji === emoji).length,
  }));
});

const total = computed(() => props.reactions.length);

const title = computed(() =>
  total.value === 1 ? '1 reação' : `${total.value} reações`
);

const contactSender = computed(() => selectedChat.value?.meta?.sender || {});

const avatarFor = reaction => {
  if (reaction?.from_contact) return contactSender.value?.thumbnail || '';
  const agent = (agents.value || []).find(
    item => Number(item.id) === Number(reaction?.user_id)
  );
  return agent?.thumbnail || '';
};

const nameFor = reaction =>
  isOwnReaction(reaction) ? 'Você' : reaction?.name || 'Contato';

// "Você" primeiro, como no WhatsApp; o resto mantem a ordem de chegada.
const visibleReactions = computed(() =>
  [...props.reactions].sort((first, second) => {
    if (isOwnReaction(first) === isOwnReaction(second)) return 0;
    return isOwnReaction(first) ? -1 : 1;
  })
);

// Só a chip da propria reacao responde ao clique — a de outra pessoa nao muda
// nada, igual ao WhatsApp.
const isMyChip = emoji =>
  emoji === ownEmoji.value || emoji === lastOwnEmoji.value;

const handleChipClick = emoji => {
  if (!isMyChip(emoji)) return;
  lastOwnEmoji.value = emoji;
  emit('react', emoji);
};

const handleRowClick = reaction => {
  if (!isOwnReaction(reaction)) return;
  lastOwnEmoji.value = reaction.emoji;
  emit('react', reaction.emoji);
};

const handlePick = emoji => {
  showPicker.value = false;
  if (!pinnedEmojis.value.includes(emoji)) pinnedEmojis.value.push(emoji);
  lastOwnEmoji.value = emoji;
  emit('react', emoji);
};

// Medicao viva da chip. Atualizada ao montar (e nao no clique: entre o
// clique e a pintura o layout ainda pode mexer) e a cada rolagem ou
// redimensionamento. O `true` no listener de scroll e o que importa — a
// conversa rola num contentor interno, e sem a fase de captura o evento
// nunca chegaria ao document.
const anchorRect = ref(null);
const syncAnchorRect = () => {
  const el = props.anchorEl;
  if (!el?.getBoundingClientRect) return;

  const rect = el.getBoundingClientRect();
  // Elemento retirado do DOM devolve tudo a zero. Nesse caso mantém-se a
  // última medição boa: com o retângulo zerado o card seria arrastado para
  // o canto superior esquerdo, longe de onde o utilizador clicou.
  if (rect.width === 0 && rect.height === 0) return;

  anchorRect.value = rect;
};

// Posiciona o card colado na chip: abaixo quando ha espaco, acima quando nao.
const cardStyle = computed(() => {
  const anchor = anchorRect.value;
  if (!anchor) return {};

  const viewportWidth = window.innerWidth;
  const viewportHeight = window.innerHeight;

  // Mensagem enviada (chip na direita): o card cresce PARA A ESQUERDA do
  // clique. Mensagem recebida (chip na esquerda): cresce para a direita.
  // Assim ele nunca avanca por cima do lado oposto da conversa.
  let left =
    props.orientation === 'right' ? anchor.right - CARD_WIDTH : anchor.left;

  // Deslocamento pedido: o card abre mais a direita do ponto de clique.
  // Numa reacao a esquerda (mensagem recebida) sobra espaco e o card anda os
  // 200px inteiros. Numa reacao a direita (mensagem enviada) a chip ja esta
  // perto da margem, entao o limite logo abaixo trava o card na borda — ou
  // seja, ele anda para a direita ate onde cabe, que e o efeito pretendido.
  left += SHIFT_RIGHT;

  if (left + CARD_WIDTH > viewportWidth - EDGE) {
    left = viewportWidth - CARD_WIDTH - EDGE;
  }
  if (left < EDGE) left = EDGE;

  const spaceBelow = viewportHeight - anchor.bottom - GAP - EDGE;
  const openDown = spaceBelow >= 220;
  const height = Math.min(
    CARD_MAX_HEIGHT,
    openDown ? spaceBelow : anchor.top - GAP - EDGE
  );

  return {
    left: `${Math.round(left)}px`,
    top: openDown ? `${Math.round(anchor.bottom + GAP)}px` : 'auto',
    bottom: openDown
      ? 'auto'
      : `${Math.round(viewportHeight - anchor.top + GAP)}px`,
    width: `${CARD_WIDTH}px`,
    maxHeight: `${Math.max(180, Math.round(height))}px`,
  };
});

const handleKeydown = event => {
  if (event.key !== 'Escape') return;
  event.preventDefault();
  if (showPicker.value) {
    showPicker.value = false;
    return;
  }
  emit('close');
};

onMounted(() => {
  pinnedEmojis.value = distinctEmojis(props.reactions);
  lastOwnEmoji.value = ownEmoji.value;
  syncAnchorRect();
  document.addEventListener('keydown', handleKeydown);
  // Captura (`true`) para apanhar tambem a rolagem do contentor da conversa,
  // que nao borbulha ate ao document.
  window.addEventListener('scroll', syncAnchorRect, true);
  window.addEventListener('resize', syncAnchorRect);
});
onBeforeUnmount(() => {
  document.removeEventListener('keydown', handleKeydown);
  window.removeEventListener('scroll', syncAnchorRect, true);
  window.removeEventListener('resize', syncAnchorRect);
});
</script>

<template>
  <div
    class="aceleres-reactions-modal fixed inset-0 z-[9999]"
    @click.self="emit('close')"
  >
    <div
      class="aceleres-reactions-modal__card fixed flex flex-col overflow-hidden rounded-2xl bg-n-solid-1 shadow-xl ring-1 ring-black/5"
      role="dialog"
      aria-modal="true"
      :aria-label="title"
      :style="cardStyle"
      @click.stop
    >
      <div class="px-4 pb-2 pt-3 text-sm font-semibold text-n-slate-12">
        {{ title }}
      </div>

      <div
        class="flex items-center gap-2 overflow-x-auto border-b border-n-weak px-4 pb-3"
      >
        <button
          type="button"
          aria-label="Escolher reação"
          class="inline-flex h-9 w-11 flex-shrink-0 items-center justify-center rounded-full border border-n-weak text-n-slate-11 transition-colors hover:bg-n-alpha-1"
          :class="{ 'bg-n-alpha-1': showPicker }"
          @click.stop="showPicker = true"
        >
          <span class="i-lucide-smile-plus h-5 w-5" />
        </button>

        <button
          v-for="chip in chips"
          :key="chip.emoji"
          type="button"
          class="inline-flex h-9 flex-shrink-0 items-center gap-1.5 rounded-full px-3 text-sm transition-colors"
          :class="
            isMyChip(chip.emoji)
              ? 'bg-n-alpha-2 font-medium text-n-slate-12 hover:bg-n-alpha-3'
              : 'cursor-default text-n-slate-11'
          "
          @click.stop="handleChipClick(chip.emoji)"
        >
          <EmojiGlyph :emoji="chip.emoji" size="reaction" />
          <span>{{ chip.count }}</span>
        </button>
      </div>

      <div class="flex-1 overflow-y-auto py-1">
        <button
          v-for="(reaction, index) in visibleReactions"
          :key="`${reaction.emoji}-${reaction.user_id}-${index}`"
          type="button"
          class="flex w-full items-center gap-3 px-4 py-2 text-left transition-colors"
          :class="
            isOwnReaction(reaction)
              ? 'cursor-pointer hover:bg-n-alpha-1'
              : 'cursor-default'
          "
          @click.stop="handleRowClick(reaction)"
        >
          <Avatar
            :name="nameFor(reaction)"
            :src="avatarFor(reaction)"
            :size="36"
            rounded-full
          />
          <div class="min-w-0 flex-1">
            <div class="truncate text-sm text-n-slate-12">
              {{ nameFor(reaction) }}
            </div>
            <div
              v-if="isOwnReaction(reaction)"
              class="truncate text-xs text-n-slate-11"
            >
              Clique para remover
            </div>
          </div>
          <EmojiGlyph
            :emoji="reaction.emoji"
            size="reaction"
            class="flex-shrink-0 !h-6 !w-6 !text-[1.5rem]"
          />
        </button>
      </div>
    </div>

    <div
      v-if="showPicker"
      class="fixed inset-0 z-[10000] flex items-center justify-center bg-black/40 px-4"
      @click.self="showPicker = false"
    >
      <WhatsAppEmojiPicker
        embedded
        show-recent-reactions
        :on-select="handlePick"
        @click.stop
      />
    </div>
  </div>
</template>
